import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  APP_BASE_URL_SCHEDULED_REPORT,
  SCHEDULED_REPORT_FREQUENCY,
  SCHEDULED_REPORT_SUBSCRIPTIONS,
  SCHEDULED_REPORT_TEMPLATES,
  SCHEDULED_REPORT_TYPES,
} from "../../../Common/EndPoints";

type User = {
  additionalinfo: null;
  customer_id: string;
  email: string;
  fcmtoken: null;
  first_name: string;
  last_name: string;
  mobileno: string;
  roleid: string;
  search_text: null;
  tenant_id: null;
  updated_ts: number;
  user_role: any;
  userid: string;
};

type ScheduledReportSubscription = {
  id: string;
  user_id: string;
  report_template_id: string;
  enable: boolean;
  sent_on: string;
  frequency: string;
  user: User;
  template: ScheduledReportTemplate;
};

export type ScheduledReportTemplateData = {
  report_types: string[];
  name: string;
  description: string;
};

export type ScheduledReportTemplate = {
  name: string;
  types: string[];
  description: string;
  id: string;
};

type MutationParams = {
  params: { id: string };
};

type MutationTemplateBody = {
  body: ScheduledReportTemplateData;
};

type ScheduledReportMutationBody = {
  body: {
    enable: boolean;
    frequency: string;
    report_template_id: string;
    send_on_hour: number;
    user_id: string;
  };
};

type ScheduledReportMutationParams = {
  params: {
    id: string;
  };
};

export const scheduledReportsApi = createApi({
  reducerPath: "scheduledReportsAPI",
  baseQuery: fetchBaseQuery({ baseUrl: APP_BASE_URL_SCHEDULED_REPORT }),
  tagTypes: ["scheduled-report_templates", "scheduled-report_subscriptions"],
  endpoints: (builder) => ({
    getReportTypes: builder.query<string[], void>({
      query: () => {
        return {
          url: SCHEDULED_REPORT_TYPES,
        };
      },
    }),
    getReportFrequency: builder.query<string[], void>({
      query: () => {
        return {
          url: SCHEDULED_REPORT_FREQUENCY,
        };
      },
    }),
    getSubscriptions: builder.query<ScheduledReportSubscription[], void>({
      query: () => {
        return {
          url: SCHEDULED_REPORT_SUBSCRIPTIONS,
        };
      },
      providesTags: ["scheduled-report_subscriptions"],
    }),
    postSubscription: builder.mutation({
      query: ({ body }: ScheduledReportMutationBody) => {
        return { url: SCHEDULED_REPORT_SUBSCRIPTIONS, method: "POST", body };
      },
      invalidatesTags: ["scheduled-report_subscriptions"],
    }),
    editSubscription: builder.mutation({
      query: ({ params, body }: ScheduledReportMutationParams & ScheduledReportMutationBody) => {
        return { url: `${SCHEDULED_REPORT_SUBSCRIPTIONS}/${params.id}`, params, method: "PUT", body };
      },
      invalidatesTags: ["scheduled-report_subscriptions"],
    }),
    deleteSubscription: builder.mutation({
      query: ({ params }: ScheduledReportMutationParams) => {
        return { url: `${SCHEDULED_REPORT_SUBSCRIPTIONS}/${params.id}`, params, method: "DELETE" };
      },
      invalidatesTags: ["scheduled-report_subscriptions"],
    }),
    getTemplates: builder.query<ScheduledReportTemplate[], void>({
      query: () => {
        return {
          url: SCHEDULED_REPORT_TEMPLATES,
        };
      },
      providesTags: ["scheduled-report_templates"],
    }),
    postTemplate: builder.mutation({
      query: ({ body }: MutationTemplateBody) => {
        return { url: SCHEDULED_REPORT_TEMPLATES, method: "POST", body };
      },
      invalidatesTags: ["scheduled-report_templates"],
    }),
    editTemplate: builder.mutation({
      query: ({ params, body }: MutationParams & MutationTemplateBody) => {
        return { url: `${SCHEDULED_REPORT_TEMPLATES}/${params.id}`, params, method: "PUT", body };
      },
      invalidatesTags: ["scheduled-report_templates"],
    }),
    deleteTemplate: builder.mutation({
      query: ({ params }: MutationParams) => {
        return { url: `${SCHEDULED_REPORT_TEMPLATES}/${params.id}`, params, method: "DELETE" };
      },
      invalidatesTags: ["scheduled-report_templates"],
    }),
  }),
});
